import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Spin, Message } from '@arco-design/web-react'
import { useTranslation } from 'react-i18next'
import { get } from '@/api/client'
import { getUserInfoApi, loginVerifyApi } from '@/api/user'
import { useAuthStore } from '@/stores/authStore'
import type { User } from '@/types'

interface OAuthCallbackData {
  action?: string
  user?: User
  token?: string
  access_token?: string
  require_verification?: boolean
  flow_token?: string
  methods?: Array<{ method: string; available: boolean }>
  id?: number
  username?: string
}

/**
 * OAuth 回调：已登录用户走 bind，未登录走 login。
 * 对齐后端 GET /api/oauth/:provider?code=&state=
 */
export default function OAuth2Callback() {
  const { provider: paramProvider = '' } = useParams()
  const provider =
    paramProvider ||
    (typeof window !== 'undefined'
      ? window.location.pathname.split('/').filter(Boolean).pop() || ''
      : '')
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [status, setStatus] = useState<'loading' | 'error'>('loading')
  const [error, setError] = useState('')
  const login = useAuthStore((s) => s.login)
  const updateUser = useAuthStore((s) => s.updateUser)
  const hasExecuted = useRef(false)

  useEffect(() => {
    if (hasExecuted.current) return
    hasExecuted.current = true

    const code = params.get('code') || params.get('access_token')
    const state = params.get('state') || ''
    if (!code) {
      setStatus('error')
      setError(t('personal.oauth.noCode'))
      return
    }

    const finish = async () => {
      try {
        const data = await get<OAuthCallbackData>(`/api/oauth/${provider}`, { code, state })

        // 绑定：后端返回绑定结果，随后刷新本地资料
        if (data?.action === 'bind') {
          Message.success(t('personal.oauth.bindSuccess'))
          const profile = await getUserInfoApi()
          updateUser(profile)
          navigate('/console/personal')
          return
        }

        // v1.0.0 起第三方登录也可能要求二次验证，用 flow_token 走 /login/verify
        if (data?.require_verification && data.flow_token) {
          const code = window.prompt(t('auth.placeholderTotp')) || ''
          if (!code) throw new Error(t('auth.enterTotpCode'))
          const verified = await loginVerifyApi({ flow_token: data.flow_token, code })
          login(verified.user, verified.token ?? null)
          Message.success(t('auth.loginSuccess'))
          navigate('/console')
          return
        }

        const userPayload = (data as { user?: User }).user
          ? (data as { user: User; access_token?: string; token?: string })
          : { user: data as unknown as User, access_token: data?.access_token, token: data?.token }

        login(userPayload.user, userPayload.access_token ?? userPayload.token ?? null)
        Message.success(t('auth.loginSuccess'))
        navigate('/console')
      } catch (err: unknown) {
        setStatus('error')
        setError(err instanceof Error ? err.message : t('personal.oauth.authFailed'))
      }
    }

    void finish()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider])

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--console-page-bg,#f8fafa)]">
      {status === 'loading' ? (
        <Spin tip={t('personal.oauth.processing')} />
      ) : (
        <div className="text-center bg-white rounded-xl shadow p-8 border border-[var(--console-border,#e2e8f0)]">
          <div className="text-red-500 font-medium mb-3">{t('personal.oauth.authFailed')}</div>
          <div className="text-sm text-[var(--console-text-muted)] mb-4">{error}</div>
          <button
            type="button"
            className="text-[var(--brand-primary,#005447)]"
            onClick={() => navigate('/console/personal')}
          >
            {t('personal.oauth.backToPersonal')}
          </button>
        </div>
      )}
    </div>
  )
}
